import React from 'react';
import { DetailsHeader } from './DetailsHeader';
import { SharedNotesTimeline } from './SharedNotesTimeline';
import { ValueLabel } from '@shared/components/ValueLabel';
import { MessageSquare, FileText, CheckCircle, Tag, Paperclip, Info } from 'lucide-react';
import { useRequestDetail } from '../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

export const ComplaintDetails = ({ data }: { data: any }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();
  const { data: requestDetail, isLoading } = useRequestDetail({
    remote_utility_id: remoteUtilityId,
    id: data?.id,
  });

  const details = requestDetail?.result || data;

  if (isLoading) return <div className="p-6 text-center text-slate-500">{t("Loading...")}</div>;

  return (
    <div className="flex flex-col h-full bg-slate-50/50 pb-6">
      <DetailsHeader 
        icon={<MessageSquare size={18} />}
        iconBgColor="bg-orange-100"
        iconTextColor="text-orange-600"
        title={details?.utilitySupportRequest?.name || t("Complaint")}
        status={details?.statusDisplay}
        id={details?.requestNo ?? t("N/A")}
        submittedDate={details?.createdDate ?? t("N/A")}
      />
      <div className="p-6 space-y-4">
        {/* COMPLAINT DETAILS & CLASSIFICATION */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><FileText size={16}/> {t("Complaint Details")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Complaint ID")} value={details?.requestNo} />
              <ValueLabel label={t("Incident Date")} value={details?.requestDate } />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><Tag size={16}/> {t("Classification")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Category")} value={details?.utilitySupportRequest?.supportRequestType || t("N/A")} />
              <ValueLabel label={t("Sub Category")} value={details?.utilitySupportRequest?.supportRequestSubtype || t("N/A")} />
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3"><Info size={16}/> {t("Complaint Description")}</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{details?.utilitySupportRequest?.longDescription || t("No description provided.")}</p>
        </div>

        {/* EXPECTED RESOLUTION */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3"><CheckCircle size={16}/> {t("Expected Resolution")}</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{t("N/A")}</p>
        </div>

        {/* EVIDENCE */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3"><Paperclip size={16}/> {t("Evidence & Attachments")}</h4>
          <div className="grid grid-cols-2 gap-3">
            {details?.file ? (
              <div className="border border-gray-100 rounded-lg p-3 flex items-center gap-3">
                <div className="bg-blue-50 text-blue-600 font-bold text-xs p-2 rounded">{t("FILE")}</div>
                <div>
                  <div className="text-sm font-medium text-gray-900">{details.file.split('/').pop()}</div>
                  <a href={details.file} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline">{t("View File")}</a>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t("No attachments provided.")}</p>
            )}
          </div>
        </div>

        {/* TABS */}
        <SharedNotesTimeline data={data} />
      </div>
    </div>
  );
};
